import { useState } from "react";
import { AlignLeft, BookOpen, Info, Plus, Target } from "lucide-react";
import Input from "../../components/Input/Input";
import Select from "../../components/Select/Select";
import Textarea from "../../components/Textarea/Textarea";
import Button from "../../components/Button/Button";
import FormSection from "./components/form/FormSection";
import NumberedListInput from "./components/form/NumberedListInput";
import TagInput from "./components/form/TagInput";
import AddSectionModal from "./components/form/AddSectionModal";
import CustomSectionBlock from "./components/form/CustomSectionBlock";
import { getPhaseMeta, createSection } from "./utils/lessonPlanUtils";

const LessonPlanForm = ({ form, errors, programData, subjectData, onFieldChange }) => {
  const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
  // The section just added from the modal gets scrolled to and focused
  const [newSectionUid, setNewSectionUid] = useState(null);

  const handleInputChange = (event) => onFieldChange(event.target.name, event.target.value);

  const updatePhase = (index, key, value) =>
    onFieldChange(
      "phases",
      form.phases.map((phase, i) => (i === index ? { ...phase, [key]: value } : phase)),
    );

  const updateSection = (uid, nextSection) =>
    onFieldChange(
      "sections",
      form.sections.map((section) => (section.uid === uid ? nextSection : section)),
    );

  const removeSection = (uid) =>
    onFieldChange(
      "sections",
      form.sections.filter((section) => section.uid !== uid),
    );

  const addSection = (section) => {
    const newSection = createSection(section);
    onFieldChange("sections", [...form.sections, newSection]);
    setNewSectionUid(newSection.uid);
    setIsSectionModalOpen(false);
  };

  return (
    <>
      {/* ================= LESSON DETAILS ================= */}
      <FormSection icon={Info} title="Lesson details" subtitle="What, who, and when.">
        <Input
          label="Lesson title"
          name="title"
          value={form.title}
          onChangeHandler={handleInputChange}
          placeholder="e.g. Photosynthesis: How plants make food"
          required
          error={errors.title}
          maxLength={255}
        />
        <div className="row">
          <div className="col-md-6 mb-3">
            <Select
              label="Class & section"
              name="program_id"
              value={form.program_id}
              options={programData}
              onChangeHandler={handleInputChange}
              placeholder="Select class"
              required
              error={errors.program_id}
            />
          </div>
          <div className="col-md-6 mb-3">
            <Select
              label="Subject"
              name="subject_id"
              value={form.subject_id}
              options={subjectData}
              onChangeHandler={handleInputChange}
              placeholder={form.program_id ? "Select subject" : "Select a class first"}
              disabled={!form.program_id}
              required
              error={errors.subject_id}
            />
          </div>
          <div className="col-md-6">
            <Input
              label="Creation Date"
              type="date"
              name="creation_date"
              value={form.creation_date}
              onChangeHandler={handleInputChange}
              required
              error={errors.creation_date}
            />
          </div>
          <div className="col-md-6">
            <Input
              label="Lesson Date"
              type="date"
              name="lesson_date"
              value={form.lesson_date}
              min={form.creation_date || undefined}
              onChangeHandler={handleInputChange}
              error={errors.lesson_date}
            />
          </div>
        </div>
        <Input
          label="Unit / chapter"
          name="unit_chapter"
          value={form.unit_chapter}
          onChangeHandler={handleInputChange}
          placeholder="e.g. Unit 4 — Life Processes"
        />
      </FormSection>

      {/* ================= LESSON FLOW ================= */}
      <FormSection icon={AlignLeft} title="Lesson flow" subtitle="Structure the session into phases.">
        <div className="lp-phase-list">
          {form.phases.map((phase, index) => {
            const meta = getPhaseMeta(phase.phase);
            return (
              <div key={phase.phase} className="lp-phase">
                <div className="lp-phase__header">
                  <span className={`lp-chip lp-chip--caps lp-tone-${meta.tone}`}>{meta.label}</span>
                  <div className="lp-phase__duration">
                    <span>Duration</span>
                    <Input
                      type="number"
                      name={`${phase.phase}_duration`}
                      id={`lp-${phase.phase}-duration`}
                      min={0}
                      max={240}
                      value={phase.duration_minutes}
                      onChangeHandler={(event) => updatePhase(index, "duration_minutes", event.target.value)}
                      aria-label={`${meta.label} duration in minutes`}
                    />
                    <span>min</span>
                  </div>
                </div>
                <Textarea
                  name={`${phase.phase}_content`}
                  id={`lp-${phase.phase}-content`}
                  rows={2}
                  value={phase.content}
                  onChangeHandler={(event) => updatePhase(index, "content", event.target.value)}
                  placeholder="Write your content here ..."
                  aria-label={`${meta.label} content`}
                />
              </div>
            );
          })}
        </div>
      </FormSection>

      {/* ================= LEARNING OBJECTIVES ================= */}
      <FormSection icon={Target} title="Learning objectives" subtitle="What students should be able to do by the end.">
        <NumberedListInput
          items={form.learning_objectives}
          onChange={(items) => onFieldChange("learning_objectives", items)}
          addLabel="Add objective"
          placeholder="e.g. Explain how plants convert light into food"
        />
      </FormSection>

      {/* ================= RESOURCES & ASSESSMENT ================= */}
      <FormSection
        icon={BookOpen}
        title="Resources & assessment"
        subtitle="Materials, homework, and how you'll measure learning."
      >
        <label htmlFor="lp-materials" className="mb-1 mt-1 text-capitalize fw-semibold">
          Materials &amp; resources
        </label>
        <TagInput
          id="lp-materials"
          tags={form.materials}
          onChange={(tags) => onFieldChange("materials", tags)}
          placeholder="e.g. Microscope, worksheet, slides"
        />

        <div className="mt-3">
          <Textarea
            label="Assessment / check for understanding"
            name="assessment"
            id="lp-assessment"
            rows={3}
            value={form.assessment}
            onChangeHandler={handleInputChange}
            placeholder="e.g. Exit ticket + cold-call review of the word equation."
          />
        </div>

        <div className="mt-3">
          <Textarea
            label="Homework"
            name="homework"
            id="lp-homework"
            rows={3}
            value={form.homework}
            onChangeHandler={handleInputChange}
            placeholder="e.g. Draw and label a leaf cross-section."
          />
        </div>
      </FormSection>

      {/* ================= CUSTOM SECTIONS ================= */}
      {form.sections.map((section) => (
        <CustomSectionBlock
          key={section.uid}
          section={section}
          isNew={section.uid === newSectionUid}
          onChange={(next) => updateSection(section.uid, next)}
          onDelete={() => removeSection(section.uid)}
        />
      ))}

      <Button variant="light" block className="lp-add-section" icon={<Plus size={16} />} onClick={() => setIsSectionModalOpen(true)}>
        Add New Section
      </Button>

      <AddSectionModal
        isOpen={isSectionModalOpen}
        onClose={() => setIsSectionModalOpen(false)}
        onAdd={addSection}
      />
    </>
  );
};

export default LessonPlanForm;
